// TOEFL iBT Mock 8 — Writing (2026 format, advanced difficulty)
//
// 1. Build a Sentence – put the word tiles in order to complete a reply.
//                       Every item has TWO distractor tiles and targets an
//                       advanced structure (negative fronting with "hardly",
//                       subjunctive "insist that + base verb", "due to a lack
//                       of", "not in as much … as", "provided that",
//                       "so … that"). `accept` lists other word orders that
//                       are also grammatical and use exactly the same tiles.
// 2. Write an Email   – 7 minutes, a formal request to be put back into a
//                       required course after a registration error.
// 3. Academic Discussion – 10 minutes, a two-part question: take a position
//                       on zoos AND name animals zoos suit poorly and say
//                       what should be done for them instead.

export const TASK_SECONDS = {
  build: 6 * 60,
  email: 7 * 60,
  discussion: 10 * 60,
};

export const buildSentenceItems = [
  {
    id: 1,
    speaker: "Why did you choose this university?",
    prefix: "Mainly",
    answer: ["because", "of", "the", "research", "opportunities", "it", "offers", "undergraduates"],
    suffix: ".",
    distractors: ["offering", "why"],
  },
  {
    id: 2,
    speaker: "Did the experiment work as expected?",
    prefix: "Not at all.",
    answer: ["Hardly", "any", "of", "the", "samples", "reacted", "the", "way", "we", "predicted"],
    suffix: ".",
    distractors: ["predicting", "much"],
  },
  {
    id: 3,
    speaker: "Have you decided where to do your internship?",
    prefix: "Not yet. I'm",
    answer: ["still", "trying", "to", "decide", "which", "offer", "to", "accept"],
    suffix: ".",
    distractors: ["deciding", "accepting"],
  },
  {
    id: 4,
    speaker: "Is the new lab manager strict?",
    prefix: "Very. She insists",
    answer: ["that", "everyone", "wear", "safety", "goggles", "at", "all", "times"],
    suffix: ".",
    distractors: ["worn", "for"],
  },
  {
    id: 5,
    speaker: "What happened to the field trip?",
    prefix: "It was cancelled",
    answer: ["due", "to", "a", "lack", "of", "interest", "among", "students"],
    suffix: ".",
    distractors: ["because", "lacking"],
  },
  {
    id: 6,
    speaker: "Why are you studying in the library instead of at home?",
    prefix: "I find",
    answer: ["it", "much", "easier", "to", "concentrate", "when", "there", "are", "no", "distractions"],
    suffix: ".",
    distractors: ["easy", "is"],
  },
  {
    id: 7,
    speaker: "Did the speaker answer your question?",
    prefix: "She did, although",
    answer: ["not", "in", "as", "much", "detail", "as", "I", "had", "hoped"],
    suffix: ".",
    distractors: ["many", "hoping"],
  },
  {
    id: 8,
    speaker: "Is it true that the museum is free on Sundays?",
    prefix: "Yes, provided",
    answer: ["that", "you", "show", "a", "valid", "student", "card", "at", "the", "entrance"],
    suffix: ".",
    distractors: ["showing", "valuable"],
  },
  {
    id: 9,
    speaker: "How was the conference in Seoul?",
    prefix: "It was",
    answer: ["so", "inspiring", "that", "I", "have", "already", "started", "planning", "my", "next", "project"],
    accept: [["so", "inspiring", "that", "I", "have", "started", "planning", "my", "next", "project", "already"]],
    suffix: ".",
    distractors: ["such", "planned"],
  },
  {
    id: 10,
    speaker: "Are you going to the party on Saturday?",
    prefix: "I'd love to, but",
    answer: ["I", "have", "to", "finish", "a", "paper", "that", "is", "due", "on", "Monday"],
    suffix: ".",
    distractors: ["finishing", "what"],
  },
];

export const emailTask = {
  recipient: "Mr. Brandt",
  scenario:
    "Last week you checked your class schedule and discovered that you had been removed from Advanced Research Methods, a course you registered for in April. The course is required for your degree and is only offered in the fall. When you called the Registrar's Office, a staff member said a system update had removed some students by mistake, but the course is now full and has a waiting list. Write an email to Mr. Brandt, the university registrar.",
  points: [
    "Explain what happened to your registration and why you believe it was an error.",
    "Describe how being removed from the course will affect you.",
    "Ask for a specific solution and suggest how the office could prevent this from happening again.",
  ],
  // Words that show each bullet point was addressed (used by the scorer).
  pointKeywords: [
    ["registered", "register", "registration", "removed", "dropped", "schedule", "april", "system", "update", "error", "mistake", "glitch", "staff", "called", "phone", "told", "confirmation", "enrolled", "enrollment"],
    ["required", "requirement", "degree", "graduate", "graduation", "delay", "semester", "year", "only offered", "fall", "full", "waiting list", "waitlist", "worried", "stress", "affect", "impact", "consequence", "financial", "cost", "scholarship", "visa"],
    ["reinstate", "reinstated", "restore", "add me", "put me back", "place", "seat", "override", "permission", "request", "ask", "would like", "please", "solution", "prevent", "in the future", "future", "notify", "notification", "email", "alert", "check", "test", "suggest", "recommend"],
  ],
  formal: true,
  minWords: 110,
  lengthSteps: [[150, 5], [125, 4.5], [105, 4], [85, 3], [65, 2], [40, 1], [1, 0.5]],
};

export const discussionTask = {
  course: "environmental ethics",
  professor: "Dr. Varga",
  question:
    "Modern zoos argue that they protect endangered species through breeding programs, research and public education. Critics respond that keeping wild animals in captivity is unethical and that the money would be better spent protecting natural habitats. Should zoos continue to exist? In your post, also identify a kind of animal that is poorly suited to life in a zoo, and explain what should be done for those animals instead.",
  posts: [
    {
      name: "Aiko",
      text: "I think zoos are still necessary. Some species, like the California condor, would probably be extinct without captive breeding. Zoos also let children see animals in person, and that experience can make them care about conservation for the rest of their lives.",
    },
    {
      name: "Tomás",
      text: "I disagree. Even the best enclosure is tiny compared to a natural range, and many animals show signs of stress in captivity. People can learn about animals from documentaries, and the money spent on zoos could protect entire ecosystems instead of a few individual animals.",
    },
  ],
  topicKeywords: [
    "zoo", "zoos", "animal", "animals", "captivity", "captive", "wild", "wildlife", "species", "endangered", "extinct", "extinction",
    "breeding", "conservation", "habitat", "habitats", "enclosure", "enclosures", "education", "research", "ethical", "unethical",
    "stress", "welfare", "sanctuary", "sanctuaries", "reserve", "reserves", "ecosystem", "ecosystems", "protect", "protection",
  ],
  // The professor asks two things; each must be answered.
  questionParts: [
    {
      label: "Take a clear position on whether zoos should continue to exist",
      keywords: ["in my view", "in my opinion", "i believe", "i think", "i agree", "i disagree", "should continue", "should exist", "should not exist", "shouldn't exist", "should be closed", "should be banned", "support", "oppose", "in favor", "against"],
    },
    {
      label: "Name an animal poorly suited to zoos and say what should be done for it instead",
      keywords: ["poorly suited", "not suited", "unsuitable", "suffer", "elephant", "elephants", "orca", "orcas", "whale", "whales", "dolphin", "dolphins", "polar bear", "polar bears", "great ape", "apes", "big cats", "large range", "roam", "migrate", "instead", "sanctuary", "sanctuaries", "reserve", "reserves", "protected area", "national park", "habitat", "release", "rewild", "wild"],
    },
  ],
  minWords: 120,
  lengthSteps: [[160, 5], [135, 4.5], [115, 4], [95, 3], [70, 2], [45, 1], [1, 0.5]],
};
